/**
 * 文件类型识别 + 彩色类型图标(人工 IM「文件类」消息用)。
 *
 * 本文件是「文件类型展示」的单一事实来源:分类规则(getFileCategory)、品牌配色、
 * 角标、类型描述 i18n key 全部以此为准。配套跨端规范见
 * docs/integration/im-file-type-display.md —— 服务侧(坐席端)需对齐同一套规则,
 * 保证两端文件类型展示一致。
 *
 * 与 icons.tsx(单色 lucide 风格 UI 控件图标)定位不同:文件类型图标是彩色品牌色块,
 * 故单独成文件;icons.tsx 仍只放单色控件图标。
 */
import type { JSX } from 'preact';

export type FileCategory = 'word' | 'excel' | 'ppt' | 'pdf' | 'archive' | 'generic';

/** 扩展名(小写,不含点)→ 类别。新增类型时此处 + 文档 + 服务侧三处同步。 */
const EXT_TO_CATEGORY: Record<string, FileCategory> = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  rtf: 'word',
  odt: 'word',
  xls: 'excel',
  xlsx: 'excel',
  csv: 'excel',
  ods: 'excel',
  ppt: 'ppt',
  pptx: 'ppt',
  odp: 'ppt',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  tgz: 'archive',
  bz2: 'archive',
};

/** 从文件名 / OSS key 取扩展名(小写,不含点);无扩展名返回 ''。先剥离路径与 query/hash。 */
function extractExt(nameOrKey: string): string {
  const base = nameOrKey.split(/[/\\]/).pop()?.split(/[?#]/)[0] ?? '';
  const dot = base.lastIndexOf('.');
  // dot<=0 排除「无点」与「.gitignore 这类纯隐藏名」;结尾即点也无效
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

/** mime 关键字兜底(扩展名判不出时用)。 */
function categoryFromMime(mime: string): FileCategory | null {
  const m = mime.toLowerCase();
  if (m.includes('pdf')) return 'pdf';
  if (m.includes('wordprocessingml') || m.includes('msword')) return 'word';
  if (m.includes('spreadsheetml') || m.includes('ms-excel') || m === 'text/csv') return 'excel';
  if (m.includes('presentationml') || m.includes('ms-powerpoint')) return 'ppt';
  if (
    m.includes('zip') ||
    m.includes('x-rar') ||
    m.includes('x-7z') ||
    m.includes('gzip') ||
    m.includes('x-tar') ||
    m.includes('compressed')
  ) {
    return 'archive';
  }
  return null;
}

/**
 * 判定文件类别。
 * 优先级:文件名扩展名 > OSS key 扩展名 > mime 兜底 > generic。
 * (历史消息只有 OSS key、无 mime/原始名;实时消息三者齐全 —— 扩展名是两条链路都稳定可用的信号。)
 */
export function getFileCategory(opts: { mime?: string; name?: string; key?: string }): FileCategory {
  const ext = extractExt(opts.name ?? '') || extractExt(opts.key ?? '');
  if (ext && EXT_TO_CATEGORY[ext]) return EXT_TO_CATEGORY[ext];
  if (opts.mime) {
    const byMime = categoryFromMime(opts.mime);
    if (byMime) return byMime;
  }
  return 'generic';
}

/** 类别 → 类型描述 i18n key(无原始文件名时作 fallback 展示)。 */
export const FILE_TYPE_LABEL_KEY: Record<FileCategory, string> = {
  pdf: 'web.fileTypePdf',
  word: 'web.fileTypeWord',
  excel: 'web.fileTypeExcel',
  ppt: 'web.fileTypePpt',
  archive: 'web.fileTypeArchive',
  generic: 'web.fileTypeGeneric',
};

/** 类别 → 品牌色(纸张填充)。 */
const FILE_TYPE_COLOR: Record<FileCategory, string> = {
  pdf: '#DC2626',
  word: '#2B579A',
  excel: '#217346',
  ppt: '#C43E1C',
  archive: '#D97706',
  generic: '#64748B',
};

/** 类别 → 角标字样(generic 无字,改画文档线)。 */
const FILE_TYPE_BADGE: Record<FileCategory, string> = {
  pdf: 'PDF',
  word: 'W',
  excel: 'X',
  ppt: 'P',
  archive: 'ZIP',
  generic: '',
};

type FileIconProps = JSX.SVGAttributes<SVGSVGElement> & {
  category: FileCategory;
  size?: number;
};

/**
 * 彩色文件类型图标:品牌色纸张(右上折角)+ 白色角标字 / 文档线。
 * viewBox 40×40 正方形,尺寸由 size 控制。
 */
export function FileTypeIcon({ category, size = 40, ...rest }: FileIconProps) {
  const color = FILE_TYPE_COLOR[category];
  const badge = FILE_TYPE_BADGE[category];
  const fontSize = badge.length === 1 ? 13 : 9;
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true" {...rest}>
      {/* 纸张主体:品牌色实心,右上折角 */}
      <path d="M11 4h14l7 7v22a3 3 0 0 1-3 3H11a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z" fill={color} />
      {/* 折角翻折高光 */}
      <path d="M25 4l7 7h-5a2 2 0 0 1-2-2z" fill="#ffffff" fill-opacity="0.3" />
      {badge ? (
        <text
          x="20"
          y="27"
          text-anchor="middle"
          fill="#ffffff"
          font-size={fontSize}
          font-weight="700"
          font-family="system-ui, -apple-system, 'Segoe UI', sans-serif"
        >
          {badge}
        </text>
      ) : (
        <g stroke="#ffffff" stroke-width="2" stroke-linecap="round">
          <line x1="13" y1="19" x2="27" y2="19" />
          <line x1="13" y1="25" x2="27" y2="25" />
          <line x1="13" y1="31" x2="22" y2="31" />
        </g>
      )}
    </svg>
  );
}
